"use client"

import { createContext, useCallback, useContext, useMemo, useState, useSyncExternalStore, type ReactNode } from "react"

import { useLocale } from "@/components/locale-provider"
import {
  detectHour12,
  persistTimeFormat,
  readStoredTimeFormat,
  resolveHour12,
  type TimeFormatPreference,
} from "@/lib/time-format"

type TimeFormatContextValue = {
  preference: TimeFormatPreference
  hour12: boolean
  setPreference: (preference: TimeFormatPreference) => void
}

const TimeFormatContext = createContext<TimeFormatContextValue>({
  preference: "auto",
  hour12: detectHour12(),
  setPreference: () => {},
})

function subscribeStoredTimeFormat(onStoreChange: () => void) {
  if (typeof window === "undefined") return () => {}
  window.addEventListener("storage", onStoreChange)
  return () => window.removeEventListener("storage", onStoreChange)
}

export function TimeFormatProvider({
  children,
  initialPreference = "auto",
}: {
  children: ReactNode
  /** From the `aient_time_format` cookie so refresh SSRs the saved segment. */
  initialPreference?: TimeFormatPreference
}) {
  const locale = useLocale()
  const [userPreference, setUserPreference] = useState<TimeFormatPreference | null>(null)
  const stored = useSyncExternalStore(
    subscribeStoredTimeFormat,
    readStoredTimeFormat,
    (): TimeFormatPreference => "auto",
  )
  const preference = userPreference ?? (stored !== "auto" ? stored : initialPreference)
  const hour12 = resolveHour12(preference, locale)

  const setPreference = useCallback((next: TimeFormatPreference) => {
    setUserPreference(next)
    persistTimeFormat(next)
  }, [])

  const value = useMemo(
    () => ({ preference, hour12, setPreference }),
    [hour12, preference, setPreference],
  )

  return <TimeFormatContext.Provider value={value}>{children}</TimeFormatContext.Provider>
}

export function useTimeFormat(): TimeFormatContextValue {
  return useContext(TimeFormatContext)
}

export function useHour12(): boolean {
  return useTimeFormat().hour12
}
